import React from "react";
import styles from "./PrivacyPolicy.module.scss";
import logoBlackSquare from "../assets/logo/MS-logo.svg";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  const currentYear = new Date().getFullYear();
  return (
    <div className={styles.PrivacyPolicy}>
      <div className={styles.grid}>
        <div className={styles.logoContainer}>
          <Link to="/">
            <img src={logoBlackSquare} />
          </Link>
        </div>
        <div className={styles.mainColumn}>
          <h1>Maximal Search Privacy Policy</h1>
          <p>
            Maximal Search ("we," "our," or "us") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal data in accordance with applicable data protection laws, including the General Data Protection Regulation (GDPR) and other relevant legislation.
          </p>
          <h2>1. Who We Are</h2>
          <p>
            Maximal Search is a recruitment and talent acquisition firm providing recruitment services to clients and candidates. We specialise in connecting professionals with opportunities across various industries while ensuring a secure and confidential hiring process.
          </p>
          <h2>2. What Information We Collect</h2>
          <p>We collect and process personal data, including but not limited to:</p>
          <ul>
            <li>
              <strong>Candidate Information:</strong> Name, contact details, employment history, education, qualifications, skills, salary expectations, CVs, references, and any additional information provided during recruitment interactions.
            </li>
            <li>
              <strong>Client Information:</strong> Business contact details, company name, job title, hiring preferences, and relevant communication records.
            </li>
            <li>
              <strong>Website and Communication Data:</strong> IP addresses, browsing activity, device information, cookies, and correspondence records.
            </li>
            <li>
              <strong>Sensitive Personal Data:</strong> Where necessary and with your explicit consent, we may process health-related information for reasonable adjustments, background checks (where legally permitted), or other specific job requirements.
            </li>
          </ul>
          <h2>3. How We Collect Your Data</h2>
          <p>We collect personal data through various sources, including:</p>
          <ul>
            <li>
              <strong>Direct Interactions:</strong> When you submit a CV, complete an application form, contact us via email or phone, or interact with us at events.
            </li>
            <li>
              <strong>Third-Party Sources:</strong> Publicly available professional networking sites (e.g., LinkedIn), job boards, referrals from clients or colleagues, and third-party recruiters.
            </li>
            <li>
              <strong>Website Analytics:</strong> Automated technologies such as cookies, tracking pixels, and analytics tools that collect technical data on website usage.
            </li>
          </ul>
          <h2>4. How We Use Your Data</h2>
          <p>We process your personal data for the following purposes:</p>
          <ul>
            <li>Providing recruitment and talent acquisition services.</li>
            <li>Assessing your suitability for roles and sharing your profile with prospective employers.</li>
            <li>Managing and maintaining our client and candidate relationships.</li>
            <li>Communicating with you regarding job opportunities, interview arrangements, and career advice.</li>
            <li>Conducting compliance and legal obligations, including right-to-work verification where applicable.</li>
            <li>Improving our services, marketing initiatives, and website functionality.</li>
          </ul>
          <h2>5. Legal Basis for Processing Data</h2>
          <p>We process your personal data under the following legal bases:</p>
          <ul>
            <li>
              <strong>Consent:</strong> When you provide your CV or agree to be contacted for job opportunities.
            </li>
            <li>
              <strong>Legitimate Interests:</strong> To operate and improve our recruitment services, including maintaining business relationships with clients and candidates.
            </li>
            <li>
              <strong>Contractual Obligations:</strong> Where necessary to facilitate employment placements or service agreements.
            </li>
            <li>
              <strong>Legal Compliance:</strong> To meet regulatory obligations, such as employment and tax laws.
            </li>
          </ul>
          <h2>6. Who We Share Your Data With</h2>
          <p>We may share your personal data with:</p>
          <ul>
            <li>
              <strong>Employers and Clients:</strong> To assess your suitability for job opportunities.
            </li>
            <li>
              <strong>Third-Party Service Providers:</strong> For administrative, legal, payroll, and compliance-related services.
            </li>
            <li>
              <strong>Regulatory Authorities:</strong> Where required by law or in response to legal requests.
            </li>
            <li>
              <strong>Technology Partners:</strong> Hosting providers, cloud storage, and analytics tools necessary for website functionality and service improvement.
            </li>
          </ul>
          <p>We do not sell or rent your data to third parties.</p>
          <h2>7. International Data Transfers</h2>
          <p>
            If we transfer your data outside of the UK or EEA, we ensure appropriate safeguards, such as standard contractual clauses, data protection agreements, or certification under relevant frameworks, to protect your information.
          </p>
          <h2>8. Data Retention</h2>
          <p>
            We retain personal data for the period necessary to fulfill the purposes outlined in this policy, considering legal and contractual obligations. Retention periods are as follows:
          </p>
          <ul>
            <li>
              <strong>Candidate Data:</strong> Typically retained for up to 5 years to facilitate future job opportunities unless you request deletion.
            </li>
            <li>
              <strong>Client Data:</strong> Retained for the duration of the business relationship and relevant statutory requirements.
            </li>
            <li>
              <strong>Financial and Compliance Data:</strong> Retained per tax, accounting, and legal obligations.
            </li>
          </ul>
          <p>If you wish to request data deletion, please contact us.</p>
          <h2>9. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>
              <strong>Access:</strong> Request a copy of the personal data we hold about you.
            </li>
            <li>
              <strong>Correction:</strong> Request correction of inaccurate or incomplete data.
            </li>
            <li>
              <strong>Erasure:</strong> Request deletion of your data where legally permissible.
            </li>
            <li>
              <strong>Restriction:</strong> Request the restriction of processing under certain conditions.
            </li>
            <li>
              <strong>Objection:</strong> Object to processing based on legitimate interests.
            </li>
            <li>
              <strong>Portability:</strong> Request to transfer your data to another service provider.
            </li>
            <li>
              <strong>Withdraw Consent:</strong> Where processing is based on consent, you can withdraw it at any time.
            </li>
          </ul>
          <p>
            To exercise your rights, please contact us at <a href="mailto:enquiries@maximalsearch.com">enquiries@maximalsearch.com</a>. We may require identity verification before processing your request.
          </p>
          <h2>10. Data Security</h2>
          <p>
            We implement industry-standard security measures, including encryption, firewalls, and access controls, to protect personal data against unauthorized access, loss, or misuse. While we take all reasonable precautions, no data transmission over the internet can be guaranteed to be completely secure.
          </p>
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in regulations or business practices. Any significant changes will be communicated via our website.
          </p>
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or how we handle your data, please contact us at: <a href="mailto:enquiries@maximalsearch.com">enquiries@maximalsearch.com</a>
          </p>
          <p>Last updated: November 25, 2025</p>
        </div>
      </div>
    </div>
  );
}